import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { logUnknownErrorResponse } from '@hungryroot/o11y';
import { type Cart, type CartProduct } from '@hungryroot/rest';
import { patchCartRequest } from '@hungryroot/rest/api/v2/customers/[id]/cart/patch';
import { type Location } from '@hungryroot/segment';
import { CartChangeType, executeRequestChangeHandlers } from '../useCartEvents';

interface AddProductMutationParams
  extends UseMutationOptions<
    Cart,
    unknown,
    { product: CartProduct; location?: Location },
    { previousCart: Cart | null }
  > {}

export function useAddProductSelf(params: AddProductMutationParams) {
  return useMutation<
    Cart,
    unknown,
    { product: CartProduct; location?: Location },
    { previousCart: Cart | null }
  >({
    mutationFn: async (params) => {
      const { product } = params;
      executeRequestChangeHandlers({
        type: CartChangeType.RequestAddProduct,
      });
      const { id: productId } = product;
      const res = await patchCartRequest({
        product_id: productId,
      });
      if (res.status !== 200) {
        logUnknownErrorResponse('Failed to add product to cart', {
          key: 'patch.cart.product',
          error: JSON.stringify(res.data, null, 2),
          productId,
        });
        throw res.data;
      }
      return res.data;
    },
    ...params,
  });
}
